import { cn } from '@/lib/utils';
import type { Dispatch, SetStateAction } from 'react';

// Define a basic interface based on the paymentConfig plans
interface Plan {
  id: string;
  price?: number;
  yearlyPrice?: number;
  credits?: {
    monthly?: number;
    yearly?: number;
    weekly?: number;
  };
  features: string[];
  yearlyFeatures?: string[];
  [key: string]: any;
}

interface PlanSelectorTableProps {
  plans: Plan[];
  selectedPlan: number;
  setSelectedPlan: Dispatch<SetStateAction<number>>;
  isTableOpen: boolean;
  setIsTableOpen: Dispatch<SetStateAction<boolean>>;
  billingInterval: 'one-time' | 'month' | 'year' | 'week';
  isYearly: boolean;
  isOneTime: boolean;
  isWeek: boolean;
  currencySymbol: string;
  getDisplayPrice: (p: number, yp: number) => number;
}

export function PlanSelectorTable({
  plans,
  selectedPlan,
  setSelectedPlan,
  isTableOpen,
  setIsTableOpen,
  billingInterval,
  isYearly,
  isOneTime,
  isWeek,
  currencySymbol,
  getDisplayPrice,
}: PlanSelectorTableProps) {
  return (
    <>
      <div className="mt-6 flex w-full justify-center pb-1">
        <button
          onClick={() => setIsTableOpen(!isTableOpen)}
          onMouseEnter={() => setIsTableOpen(true)}
          className="group mb-2 flex h-3 w-16 cursor-pointer items-center justify-center rounded-full bg-gray-200 shadow-sm transition-all hover:bg-gray-300 dark:bg-zinc-700 dark:hover:bg-zinc-600/80"
          title={isTableOpen ? 'Hide all plans' : 'Show all plans'}
          aria-label={isTableOpen ? 'Hide all plans' : 'Show all plans'}
          aria-expanded={isTableOpen}
        >
          <div
            className={cn(
              'h-1 rounded-full bg-gray-400 transition-all duration-300 group-hover:bg-indigo-600 dark:bg-zinc-300',
              isTableOpen ? 'w-10' : 'w-6'
            )}
          />
        </button>
      </div>

      <div
        className={cn(
          '-mx-1 grid rounded-lg bg-white/50 backdrop-blur-sm transition-[grid-template-rows] duration-300 ease-in-out dark:bg-black/20',
          isTableOpen ? 'mb-0 grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mb-1 grid grid-cols-[1.5fr_1.2fr_1fr_1fr] gap-2 px-3 pt-2 font-bold text-[9px] text-gray-400 uppercase tracking-tight dark:text-zinc-500">
            <div className="whitespace-nowrap text-left">
              SELECT PLAN <span className="text-[8px] normal-case opacity-70">up to</span>
            </div>
            <div className="text-right">EACH</div>
            <div className="text-right">CREDITS</div>
            <div className="text-right">PRICE</div>
          </div>
          <div className="mx-1 mb-1 flex flex-col divide-y divide-gray-100 overflow-hidden rounded-md border border-gray-200 bg-white dark:divide-[rgba(255,255,255,0.05)] dark:border-[rgba(255,255,255,0.1)] dark:bg-[#2d2d2d]">
            {plans.map((plan, idx) => {
              const isSelected = selectedPlan === idx;
              const c =
                billingInterval === 'year'
                  ? plan.credits?.yearly || 0
                  : billingInterval === 'week'
                    ? plan.credits?.weekly || 0
                    : plan.credits?.monthly || 0;
              const v = Math.floor(c / 30);
              const imagesCount = Math.floor(c / 0.8);
              const p = getDisplayPrice(plan.price || 0, plan.yearlyPrice || 0);
              const costPerVideo = p / v;
              const costPerImage = p / imagesCount;

              const formatCost = (cost: number) => {
                if (cost < 1) {
                  if (currencySymbol === '£') return `${Math.round(cost * 100)}p`;
                  if (currencySymbol === '$') return `${Math.round(cost * 100)}¢`;
                  return `${currencySymbol}${cost.toFixed(2)}`;
                }
                return `${currencySymbol}${cost.toFixed(2)}`;
              };

              return (
                <button
                  key={plan.id}
                  onClick={() => setSelectedPlan(idx)}
                  className={cn(
                    'group relative w-full cursor-pointer px-3 py-2 text-sm transition-colors',
                    isSelected
                      ? 'bg-indigo-50 text-gray-900 shadow-inner dark:bg-indigo-950/20 dark:text-gray-100'
                      : 'font-normal text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-zinc-800/30'
                  )}
                >
                  {isSelected && (
                    <div className="absolute top-0 bottom-0 left-0 w-1 bg-indigo-600 dark:bg-indigo-500" />
                  )}
                  <div className="relative z-10 grid w-full grid-cols-[1.5fr_1.2fr_1fr_1fr] items-center gap-2 font-inherit text-sm">
                    <div className="flex flex-col items-start gap-[2px]">
                      <div
                        className={cn(
                          'flex items-baseline gap-1.5',
                          isSelected
                            ? 'font-semibold text-gray-900 dark:text-gray-100'
                            : 'font-normal text-gray-700 dark:text-zinc-300'
                        )}
                      >
                        <span className="truncate">{v.toLocaleString()} VIDEOS</span>
                        <span
                          className={cn(
                            'shrink-0 font-bold text-[10px] uppercase',
                            isSelected
                              ? 'text-gray-500 dark:text-zinc-400'
                              : 'text-gray-400 dark:text-zinc-500'
                          )}
                        >
                          OR
                        </span>
                      </div>
                      <div
                        className={cn(
                          'truncate text-[11px] tracking-tight',
                          isSelected
                            ? 'font-medium text-indigo-600 dark:text-indigo-400'
                            : 'font-normal text-gray-500 dark:text-zinc-400'
                        )}
                      >
                        {imagesCount.toLocaleString()} IMAGES
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-[2px]">
                      <div
                        className={cn(
                          'text-[11px]',
                          isSelected
                            ? 'font-medium text-gray-700 dark:text-zinc-300'
                            : 'font-normal text-gray-500 dark:text-zinc-400'
                        )}
                      >
                        {formatCost(costPerVideo)}/video
                      </div>
                      <div
                        className={cn(
                          'whitespace-nowrap text-[10px]',
                          isSelected
                            ? 'font-medium text-gray-500 dark:text-zinc-400'
                            : 'font-normal text-gray-400 dark:text-zinc-500'
                        )}
                      >
                        {formatCost(costPerImage)}/image
                      </div>
                    </div>
                    <div
                      className={cn(
                        'text-right font-bold',
                        isSelected
                          ? 'text-gray-900 dark:text-gray-100'
                          : 'text-gray-700 dark:text-zinc-300'
                      )}
                    >
                      {c.toLocaleString()}
                    </div>
                    <div className="flex flex-col items-end gap-[2px]">
                      <div
                        className={cn(
                          'font-bold',
                          isSelected
                            ? 'text-gray-900 dark:text-gray-100'
                            : 'text-gray-700 dark:text-zinc-300'
                        )}
                      >
                        {currencySymbol}
                        {p}
                      </div>
                      <div
                        className={cn(
                          'font-medium text-[9px] uppercase tracking-wide',
                          isSelected
                            ? 'text-gray-500 dark:text-zinc-400'
                            : 'text-gray-400 dark:text-zinc-500'
                        )}
                      >
                        {billingInterval === 'year'
                          ? 'YEARLY'
                          : billingInterval === 'week'
                            ? 'WEEKLY'
                            : billingInterval === 'one-time'
                              ? 'ONCE'
                              : 'MONTHLY'}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
